import { useState } from "react";
import { BarChart, Bar as RBar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import { PageHeader, Panel, Loading, ErrorBox, Warnings, Bar } from "@/components/Widgets";
import { useApi, nf, COLORS } from "@/lib/api";

const COUNTRY = { IT: "Italia", CH: "Svizzera", ES: "Spagna", DE: "Germania", FR: "Francia", GB: "Regno Unito", US: "USA", AT: "Austria", SM: "San Marino", BR: "Brasile" };

const RankList = ({ items, color, testid, map }) => {
  const max = Math.max(...items.map((i) => i.value), 1);
  const tot = items.reduce((s, i) => s + i.value, 0) || 1;
  return (
    <ul className="space-y-3" data-testid={testid}>
      {items.map((it) => (
        <li key={it.name}>
          <div className="flex justify-between text-sm mb-1"><span>{map?.[it.name] || it.name}</span><span className="font-mono-num text-xs">{nf(it.value)} · {((it.value / tot) * 100).toFixed(1)}%</span></div>
          <Bar value={it.value} max={max} color={color} />
        </li>
      ))}
      {!items.length && <li className="text-sm text-[#5C5C58]">Dati non disponibili (servono almeno 100 follower/interazioni).</li>}
    </ul>
  );
};

export default function Audience() {
  const { data, error, loading } = useApi("/demographics");
  const [seg, setSeg] = useState("follower");
  if (loading) return <Loading />;
  if (error) return <ErrorBox msg={error} />;
  const d = data[seg] || { age_gender: [], city: [], country: [] };
  const g = d.age_gender.reduce((a, r) => ({ F: a.F + r.F, M: a.M + r.M, U: a.U + r.U }), { F: 0, M: 0, U: 0 });
  const gender = [{ name: "Donne", value: g.F }, { name: "Uomini", value: g.M }, { name: "Non specificato", value: g.U }];
  const tot = g.F + g.M + g.U || 1;
  const topAge = [...d.age_gender].sort((a, b) => b.F + b.M + b.U - (a.F + a.M + a.U))[0];
  return (
    <>
      <PageHeader eyebrow="Instagram · dati aggregati Meta" title="Chi ti segue e chi interagisce" desc="Confronta la demografica dei follower con quella di chi mette like, commenta, salva e condivide: è qui che si nasconde il tuo cliente ideale.">
        <div className="flex border border-[#E8E8E4] bg-white">
          {[["follower", "Follower"], ["engaged", "Pubblico coinvolto"]].map(([k, l]) => (
            <button key={k} data-testid={`segment-${k}`} onClick={() => setSeg(k)} className={`px-4 py-2 text-sm transition-colors ${seg === k ? "bg-[#2A4B3C] text-white" : "hover:bg-[#F7F7F5]"}`}>{l}</button>
          ))}
        </div>
      </PageHeader>
      <Warnings list={data.warnings} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-[#2A4B3C] text-[#F7F7F5] p-6 rise" data-testid="persona-card">
          <div className="label !text-[#C9D6CE]">Profilo prevalente</div>
          <div className="font-display text-3xl mt-3 font-semibold">{topAge ? `${topAge.age} anni` : "-"}</div>
          <p className="text-sm mt-2 text-[#DDE5E0]">{g.F >= g.M ? "Prevalenza donne" : "Prevalenza uomini"} ({((Math.max(g.F, g.M) / tot) * 100).toFixed(0)}%) · città principale: {d.city[0]?.name || "-"}</p>
        </div>
        <Panel testid="gender-panel" title="Genere">
          <div className="flex items-center gap-4">
            <div className="h-32 w-32"><ResponsiveContainer><PieChart><Pie data={gender} dataKey="value" innerRadius={35} outerRadius={60} strokeWidth={2}>{gender.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer></div>
            <ul className="text-sm space-y-2">{gender.map((x, i) => <li key={x.name} className="flex items-center gap-2"><span className="w-2 h-2" style={{ background: COLORS[i] }} />{x.name}<span className="font-mono-num text-xs ml-auto pl-3">{((x.value / tot) * 100).toFixed(1)}%</span></li>)}</ul>
          </div>
        </Panel>
        <Panel testid="total-panel" title="Totale campione">
          <div className="font-mono-num text-4xl">{nf(tot)}</div>
          <p className="text-xs text-[#5C5C58] mt-2">{seg === "follower" ? "Follower con dati demografici" : "Account che hanno interagito (mese corrente)"}</p>
        </Panel>
      </div>
      <Panel testid="age-gender-chart" title="Età × genere" className="mb-4">
        <div className="h-72"><ResponsiveContainer><BarChart data={d.age_gender}><CartesianGrid stroke="#F1F1EC" vertical={false} /><XAxis dataKey="age" tickLine={false} axisLine={false} /><YAxis tickLine={false} axisLine={false} width={40} /><Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
          <RBar dataKey="F" name="Donne" stackId="a" fill="#2A4B3C" /><RBar dataKey="M" name="Uomini" stackId="a" fill="#C05C3D" /><RBar dataKey="U" name="N/D" stackId="a" fill="#B9A88F" />
        </BarChart></ResponsiveContainer></div>
      </Panel>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Città principali" sub="Top 15"><RankList testid="city-list" items={d.city} color="#2A4B3C" /></Panel>
        <Panel title="Paesi" sub="Top 15"><RankList testid="country-list" items={d.country} color="#C05C3D" map={COUNTRY} /></Panel>
      </div>
      <p className="text-xs text-[#5C5C58] mt-6">Nota: Meta non fornisce più dati demografici per le Pagine Facebook tramite API; la demografica mostrata è quella Instagram (follower e pubblico coinvolto), aggregata e anonima.</p>
    </>
  );
}
